/**
 * Строки продукта: каталоги, активная локаль и `t()`.
 *
 * ─── Чем это отличается от «словаря» ────────────────────────────────────────
 *
 * Замена строк по ключу — это половина работы, и не та, на которой ломаются.
 * Ломаются на числе: «1 бренд», «2 бренда», «5 брендов» — три формы в русском,
 * две в английском и **шесть** в арабском (`zero`, `one`, `two`, `few`, `many`,
 * `other`). Склейка «число + слово» этого не выражает ни на одном из трёх
 * языков сразу, поэтому строка хранится целиком на языке ICU MessageFormat:
 *
 *     'switcher.brands': '{count, plural, one {# бренд} few {# бренда} other {# брендов}}'
 *
 * Формы выбирает `Intl.PluralRules` активной локали — то есть браузер, а не мы.
 * Никакой таблицы правил в репозитории нет и быть не должно: правила арабского
 * мы не знаем, а CLDR знает.
 *
 * ─── Почему `intl-messageformat`, а не i18next ──────────────────────────────
 *
 * Требование — ICU MessageFormat (план глобала, B1). `intl-messageformat` это
 * ровно он и ничего сверх: разбор, выбор формы через штатный `Intl`, вставки
 * разметки. Ни загрузчиков, ни определителей языка, ни системы плагинов,
 * ни бэкендов — всего того, из чего на три четверти состоит i18next и что этому
 * репозиторию не нужно: каталогов три, они лежат в сборке, локаль приходит из
 * учётной записи. Данных CLDR пакет с собой не тащит — формы множественного
 * числа берутся из `Intl.PluralRules`, который есть в каждом браузере из
 * поддерживаемых.
 *
 * И главное: он не знает про React. Лендинг — Astro, кабинет — React, приёмки —
 * Node; одна и та же функция работает во всех трёх, а React-обвязка живёт
 * отдельно, в `app/src/i18n`.
 *
 * ─── Неразрывные пробелы ────────────────────────────────────────────────────
 *
 * `t()` их **не** расставляет. Каталоги лежат в `shared/src` и `app/src`, то
 * есть под тем же типографским проходом сборки (`app/typo-plugin.ts`), что и
 * строки, набранные прямо в разметке: строка в каталоге и строка в компоненте
 * обрабатываются одинаково. Лендинг зовёт `typo()` руками — как звал до B1.
 * Второй проход в `t()` означал бы, что одна и та же строка получает
 * неразрывные пробелы дважды и по-разному: на сборке — по исходному тексту, в
 * браузере — уже по подставленным числам.
 */

import IntlMessageFormat from 'intl-messageformat';

import { LOCALE_TAG, type Locale } from './locale';
import { market } from './market';

/** Каталог одной локали: ключ → строка на языке ICU MessageFormat. */
export type Catalog = Readonly<Record<string, string>>;

/**
 * Откуда берётся каталог набора для локали.
 *
 * Синхронный ответ — для той локали, что уехала в сборку обычным `import`;
 * обещание — для остальных, которые подтягиваются `import()` отдельным куском.
 * Так РФ-бандл не носит с собой ни английских, ни арабских строк.
 */
export type CatalogLoader = (locale: Locale) => Catalog | Promise<Catalog>;

/** Значения подстановок. Числа и даты форматирует сам ICU. */
export type Values = Record<string, string | number | boolean | Date | null | undefined>;

/**
 * Значения с разметкой: `<b>…</b>` в строке зовёт функцию `b`.
 *
 * Тип обвязки задаётся вызывающим: в кабинете это `ReactNode`, в приёмке —
 * строка. Сам слой про React не знает.
 */
export type RichValues<T> = Record<
  string,
  string | number | boolean | Date | null | undefined | ((chunks: (string | T)[]) => T)
>;

/* ────────────────────────────── наборы каталогов ─────────────────────────── */

const loaders = new Map<string, CatalogLoader>();

/** Разобранные каталоги по локали: имя набора → каталог. */
const loaded = new Map<Locale, Map<string, Catalog>>();

let activeLocale: Locale = market().defaultLocale;

/** Плоский каталог активной локали и запасного языка рынка — то, что читает `t()`. */
let messages: Record<string, string> = {};

const listeners = new Set<() => void>();

/** Разобранные сообщения: ключ `локаль | ключ`. Разбор ICU не бесплатный. */
const compiled = new Map<string, IntlMessageFormat>();

/** О чём уже пожаловались: одна жалоба на ключ, а не по одной на кадр. */
const complained = new Set<string>();

/**
 * Порядок поиска строки: активная локаль, затем **язык-источник рынка**.
 *
 * Запасной язык — не «английский всегда», а тот, на котором строки этого рынка
 * написаны первыми: у глобала это `en` (арабский переводится с него), у РФ —
 * `ru`. Подставлять глобальному клиенту русскую строку вместо недостающей
 * арабской нельзя, и наоборот тоже.
 *
 * На совпадении цепочка из одного звена: второй каталог не грузится вовсе, и
 * РФ-бандл остаётся ровно таким, каким был.
 */
function chain(locale: Locale): Locale[] {
  const source = market().defaultLocale;
  return locale === source ? [locale] : [locale, source];
}

function flatten(locale: Locale): Record<string, string> {
  const out: Record<string, string> = {};
  // С конца: активная локаль перекрывает запасную.
  for (const step of chain(locale).reverse()) {
    for (const catalog of loaded.get(step)?.values() ?? []) Object.assign(out, catalog);
  }
  return out;
}

function put(locale: Locale, name: string, catalog: Catalog): void {
  let bucket = loaded.get(locale);
  if (!bucket) {
    bucket = new Map();
    loaded.set(locale, bucket);
  }
  bucket.set(name, catalog);
}

function announce(): void {
  for (const listener of listeners) listener();
}

/**
 * Объявить набор каталогов.
 *
 * Наборов больше одного нарочно: общий слой знает ошибки API и единицы, кабинет
 * — свои экраны, лендинг — свои страницы. Разрезано это не ради порядка, а
 * ради веса: кабинету незачем носить тексты витрины, а витрине — тексты
 * кабинета.
 *
 * Если каталог активной локали отдаётся синхронно, он встаёт сразу — до первой
 * отрисовки и без ожидания. Именно поэтому РФ-кабинет не изменился: `ru`
 * приезжает обычным `import`, а `setLocale` в нём никто не зовёт.
 */
export function registerCatalog(name: string, loader: CatalogLoader): void {
  loaders.set(name, loader);
  const value = loader(activeLocale);
  if (value instanceof Promise) {
    void value.then((catalog) => {
      put(activeLocale, name, catalog);
      messages = flatten(activeLocale);
      announce();
    });
    return;
  }
  put(activeLocale, name, value);
  messages = flatten(activeLocale);
}

/** Активная локаль. */
export function locale(): Locale {
  return activeLocale;
}

/** Тег BCP-47 активной локали — им разговаривают `Intl` и ICU. */
export function localeTag(): string {
  return LOCALE_TAG[activeLocale];
}

/**
 * Переключить локаль: подтянуть каталоги и объявить подписчикам.
 *
 * Обещание, а не мгновенная замена, потому что каталоги неактивных локалей
 * лежат отдельными кусками сборки. До того как обещание сбудется, на экране
 * стоит прежний язык — это лучше, чем полкабинета без строк.
 */
export async function setLocale(next: Locale): Promise<void> {
  if (next === activeLocale && loaded.has(next)) return;

  const previous = activeLocale;
  activeLocale = next;

  const wanted = chain(next);
  await Promise.all(
    wanted.flatMap((step) =>
      [...loaders].map(async ([name, loader]) => {
        if (loaded.get(step)?.has(name)) return;
        put(step, name, await loader(step));
      }),
    ),
  );

  messages = flatten(next);
  if (previous !== next) compiled.clear();
  announce();
}

/** Подписка на смену локали. Возвращает отписку. */
export function onLocaleChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/* ─────────────────────────────────── t() ─────────────────────────────────── */

/**
 * Строка по ключу. Ключа нет — возвращается сам ключ и раздаётся жалоба.
 *
 * Пустая строка сюда не возвращается никогда: на экране она читается как
 * оторванная подпись, и найти такое место потом нельзя. Ключ на экране
 * некрасив, зато называет себя. Расхождение каталогов ловится раньше — приёмкой
 * `tools/check-i18n.mjs` в `npm run check`.
 */
function lookup(key: string): string | null {
  const message = messages[key];
  if (message !== undefined) return message;
  if (!complained.has(key)) {
    complained.add(key);
    console.warn(`i18n: нет строки «${key}» для локали ${activeLocale}`);
  }
  return null;
}

function formatter(key: string, message: string): IntlMessageFormat {
  const cacheKey = `${activeLocale} | ${key}`;
  let found = compiled.get(cacheKey);
  if (!found) {
    found = new IntlMessageFormat(message, LOCALE_TAG[activeLocale]);
    compiled.set(cacheKey, found);
  }
  return found;
}

/**
 * Строка по ключу с подстановками. Плюрализация, выбор по значению и
 * форматирование чисел внутри строки — всё это ICU, а не склейка на месте.
 */
export function t(key: string, values?: Values): string {
  const message = lookup(key);
  if (message === null) return key;
  // Строка без подстановок — самый частый случай; разбирать её незачем.
  if (!values && !message.includes('{')) return message;
  try {
    return String(formatter(key, message).format(values));
  } catch (error) {
    console.error(`i18n: строка «${key}» не собралась`, error);
    return message;
  }
}

/**
 * То же со вставками разметки: `Условия — в <a>оферте</a>` собирает кусок с
 * настоящей ссылкой, а не со склейкой из трёх строк.
 *
 * Возвращается массив кусков — строк и того, что вернули обвязки. Превращать
 * его в узел React — дело `app/src/i18n`: общий слой про React не знает.
 */
export function tParts<T>(key: string, values: RichValues<T>): (string | T)[] {
  const message = lookup(key);
  if (message === null) return [key];
  try {
    const out = formatter(key, message).format<T>(values as never);
    if (Array.isArray(out)) return out as (string | T)[];
    return [out as string | T];
  } catch (error) {
    console.error(`i18n: строка «${key}» не собралась`, error);
    return [message];
  }
}

/** Есть ли такая строка. Нужно приёмкам и переключателям, а не экранам. */
export function hasMessage(key: string): boolean {
  return messages[key] !== undefined;
}
